import React, { createContext, useContext, useState, useCallback, useMemo } from "react";

interface ExportData {
  locationId: number;
  locationName: string;
  dashboardSection: string;
  ghgScope: string[];
  startYear?: number;
  endYear?: number;
  scenario?: string;
  businessUnit?: string;
  measureCount: number;
  scenarioOptions?: string[];
}

interface ChartRefs {
  mac: React.RefObject<SVGSVGElement> | null;
  timeline: React.RefObject<SVGSVGElement> | null;
  measureImpact: React.RefObject<SVGSVGElement> | null;
  glidepath: React.RefObject<SVGSVGElement> | null;
}

interface ExportPackContextType {
  isDrawerOpen: boolean;
  openDrawer: () => void;
  closeDrawer: () => void;
  locationId: number;
  locationName: string;
  measureCount: number;
  dashboardSection: string;
  ghgScope: string[];
  startYear: number | null;
  endYear: number | null;
  scenario: string | null;
  businessUnit: string | null;
  chartRefs: ChartRefs;
  scenarioOptions: string[];
  setExportData: (data: ExportData) => void;
  setChartRefs: (refs: ChartRefs) => void;
}

const ExportPackContext = createContext<ExportPackContextType>({
  isDrawerOpen: false,
  openDrawer: () => {},
  closeDrawer: () => {},
  locationId: 0,
  locationName: "",
  measureCount: 0,
  dashboardSection: "",
  ghgScope: [],
  startYear: null,
  endYear: null,
  scenario: null,
  businessUnit: null,
  chartRefs: { mac: null, timeline: null, measureImpact: null, glidepath: null },
  scenarioOptions: [],
  setExportData: () => {},
  setChartRefs: () => {},
});

export const ExportPackProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [locationId, setLocationId] = useState(0);
  const [locationName, setLocationName] = useState("");
  const [measureCount, setMeasureCount] = useState(0);
  const [dashboardSection, setDashboardSection] = useState("");
  const [ghgScope, setGhgScope] = useState<string[]>([]);
  const [startYear, setStartYear] = useState<number | null>(null);
  const [endYear, setEndYear] = useState<number | null>(null);
  const [scenario, setScenario] = useState<string | null>(null);
  const [businessUnit, setBusinessUnit] = useState<string | null>(null);
  const [scenarioOptions, setScenarioOptions] = useState<string[]>([]);
  const [chartRefs, setChartRefs] = useState<ChartRefs>({
    mac: null,
    timeline: null,
    measureImpact: null,
    glidepath: null,
  });

  const openDrawer = useCallback(() => setIsDrawerOpen(true), []);
  const closeDrawer = useCallback(() => setIsDrawerOpen(false), []);

  const setExportData = useCallback((data: ExportData) => {
    setLocationId(data.locationId);
    setLocationName(data.locationName);
    setDashboardSection(data.dashboardSection);
    setGhgScope(data.ghgScope);
    setStartYear(data.startYear ?? null);
    setEndYear(data.endYear ?? null);
    setScenario(data.scenario ?? null);
    setBusinessUnit(data.businessUnit ?? null);
    setMeasureCount(data.measureCount);
    setScenarioOptions(data.scenarioOptions ?? []);
  }, []);

  const contextValue = useMemo(() => ({
    isDrawerOpen,
    openDrawer,
    closeDrawer,
    locationId,
    locationName,
    measureCount,
    dashboardSection,
    ghgScope,
    startYear,
    endYear,
    scenario,
    businessUnit,
    chartRefs,
    scenarioOptions,
    setExportData,
    setChartRefs,
  }), [
    isDrawerOpen,
    openDrawer,
    closeDrawer,
    locationId,
    locationName,
    measureCount,
    dashboardSection,
    ghgScope,
    startYear,
    endYear,
    scenario,
    businessUnit,
    chartRefs,
    scenarioOptions,
    setExportData,
    setChartRefs,
  ]);

  return (
    <ExportPackContext.Provider value={contextValue}>
      {children}
    </ExportPackContext.Provider>
  );
};

export const useExportPack = () => useContext(ExportPackContext);
